import React from "react";
import { Modal, Tooltip, Button, message, Segmented, Tag } from "antd";
import { malimaxTwoById } from "../../../../libs/apis";
import { MalimaxTwoI } from "../../../../libs/interfaces";
import { Box } from "@mui/material";
import type { Dayjs } from "dayjs";
import {
  CloseOutlined,
  FundViewOutlined,
  InfoCircleOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { OneCard } from "../../../../libs/shared";
import "../../../malimax-one/components/list/show-container.css";

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
  // type?: "video-validation" | "video-charging" | "video-container";
  type?:any;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
  type: "validation";
}

interface ShowContainerPropsI {
  id: number;
  isError?: boolean;
}

export const ShowContainer = ({ id, isError }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) setOpen(true);
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer
          open={open}
          onClose={handleClose}
          id={id}
          isError={isError}
        />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────── */
/*  Helper: single info field                      */
/* ─────────────────────────────────────────────── */
const Field = ({
  label,
  value,
  full,
}: {
  label: string;
  value?: string | null;
  full?: boolean;
}) => (
  <div className={`info-block${full ? " full-width" : ""}`}>
    <div className="label">{label}</div>
    <div className={`value${!value ? " empty" : ""}`}>{value || "—"}</div>
  </div>
);

/* ─────────────────────────────────────────────── */
/*  Form info panel                                */
/* ─────────────────────────────────────────────── */
const FormPanel = ({ data }: { data: MalimaxTwoI }) => (
  <div className="info-grid">
    {/* Tiempos */}
    <div className="time-row">
      <div className="time-badge">
        <span className="t-label">Hora inicio</span>
        <span className="t-value">{data.hourInit || "—"}</span>
      </div>
      <div className="time-badge">
        <span className="t-label">Hora fin</span>
        <span className="t-value">{data.hourEnd || "—"}</span>
      </div>
      <div className="time-badge">
        <span className="t-label">Guardado</span>
        <span className="t-value">
          {data.hourSaveUser
            ? data.hourSaveUser
            : data.timeStampSave
            ? new Date(data.timeStampSave as any).toLocaleTimeString()
            : "—"}
        </span>
      </div>
    </div>

    {/* General */}
    <div className="form-section-title">General</div>
    <Field label="Cliente" value={data.client} />
    <Field label="ID Cliente" value={data.clientIdentification} />
    <Field label="Producto" value={data.product} />
    <Field label="Presentación" value={data.presentation} />
    <Field label="N° Pallets" value={data.numberPallet} />
    <Field label="N° Presentaciones" value={data.numberPresentation} />
    <Field label="N° Muestreo" value={data.numberSampling} />
    <Field label="Coordenadas" value={data.coordinates} />

    {/* Personas presentes */}
    {!!data.peoplePresent?.length && (
      <>
        <div className="form-section-title">
          Personas Presentes en la Operativa
        </div>
        {data.peoplePresent.map((p, i) => (
          <Field
            key={i}
            label={`Persona ${i + 1}`}
            value={[p.name, p.identification && `C.I. ${p.identification}`, p.position]
              .filter(Boolean)
              .join(" — ")}
            full
          />
        ))}
      </>
    )}

    {/* Observaciones */}
    {data.observation && (
      <>
        <div className="form-section-title">Observaciones</div>
        <Field label="Observación" value={data.observation} full />
      </>
    )}
  </div>
);

/* ─────────────────────────────────────────────── */
/*  Photos panel                                   */
/* ─────────────────────────────────────────────── */
const PhotosPanel = ({
  cards,
  totalPhotos,
}: {
  cards: OneCardI[];
  totalPhotos: string[];
}) => (
  <Box>
    <div className="section-header">
      <h3>Proceso de Carga</h3>
    </div>
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      }}
    >
      {cards
        .filter((el) => el.type === "validation")
        .map((el, i) => (
          <OneCard
            data={el}
            key={`${el.title}-${i}`}
            lasFotos={totalPhotos}
          />
        ))}
    </Box>
  </Box>
);

/* ─────────────────────────────────────────────── */
/*  Modal                                          */
/* ─────────────────────────────────────────────── */
interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
  isError?: boolean;
}

export const ModalContainer = ({
  open,
  onClose,
  id,
  isError,
}: ModalContainerPropsI) => {
  const [isLoading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<MalimaxTwoI>({
    container: "",
  } as MalimaxTwoI);
  const [cards, setCards] = React.useState<OneCardI[]>([]);
  const [totalPhotos, setTotalPhotos] = React.useState<string[]>([]);
  const [tab, setTab] = React.useState<string>("form");

  React.useEffect(() => {
    handleGetData();
    return () => {
      setCards([]);
      setData({ container: "" } as MalimaxTwoI);
      setLoading(true);
      setTab("form");
    };
  }, []);

  const handleGetData = async () => {
    try {
      const { data: dataContainerGet } = await malimaxTwoById(id);
      if (!dataContainerGet) return;
      setData(dataContainerGet);

      const arrCards: OneCardI[] = dataContainerGet.images.map((photo) => ({
        title: "Fotos",
        noLine: true,
        photos: [{ src: photo.src, alt: photo.comment, type: "validation" }],
        value: photo.comment ?? "",
        type: "validation",
      }));

      setTotalPhotos(dataContainerGet.images.map((photo) => photo.src));
      setCards(arrCards);
      setLoading(false);
    } catch (err: any) {
      message.error((err as Error).message);
      setLoading(false);
    }
  };

  const photoCount = cards.filter((c) => c.photos?.[0]?.src).length;

  return (
    <Modal
      title={
        <p className="modal-header-gradient">
          {`CONTENEDOR N° ${data.container}`}
          {data.statusWorkflow && (
            <Tag
              color={data.statusWorkflowColor || "#25D228"}
              style={{
                marginLeft: 10,
                fontSize: "0.75rem",
                verticalAlign: "middle",
              }}
            >
              {data.statusWorkflow}
            </Tag>
          )}
        </p>
      }
      closeIcon={
        <CloseOutlined
          style={{
            background: "red",
            color: "white",
            padding: "5px",
            borderRadius: "5px",
          }}
        />
      }
      footer={null}
      loading={isLoading}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={820}
      centered
      zIndex={1602}
      destroyOnClose
      style={{ padding: 0 }}
    >
      {/* Segmented control */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5, mt: 0.5 }}>
        <Segmented
          className="modal-segmented"
          value={tab}
          onChange={(v) => setTab(v as string)}
          options={[
            {
              label: (
                <span>
                  <InfoCircleOutlined style={{ marginRight: 5 }} />
                  Información
                </span>
              ),
              value: "form",
            },
            {
              label: (
                <span>
                  <PictureOutlined style={{ marginRight: 5 }} />
                  Fotos
                  {photoCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        background: "#25D228",
                        color: "white",
                        borderRadius: 10,
                        padding: "0 6px",
                        fontSize: "0.7rem",
                      }}
                    >
                      {photoCount}
                    </span>
                  )}
                </span>
              ),
              value: "photos",
            },
          ]}
          style={{ fontWeight: 600 }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ overflowY: "auto", maxHeight: 520, p: 1.5 }}>
        {tab === "form" ? (
          <FormPanel data={data} />
        ) : (
          <PhotosPanel cards={cards} totalPhotos={totalPhotos} />
        )}
      </Box>
    </Modal>
  );
};